import { useCallback, useEffect, useMemo, useState } from "react";
import billReminder from "@examples/bill-reminder.json";
import inboundRestored from "@examples/inbound-restored.json";
import outageNotify from "@examples/outage-notify.json";
import { api, ApiError, type ApiErrorDetail } from "../../api/client";
import { readCsrfCookie } from "../auth/csrfCookie";
import { canRedo, canUndo, initHistory, pushHistory, redoHistory, undoHistory } from "./documentHistory";
import { loadDraft, saveDraft } from "./draftStorage";
import { addEdge, nextEdgeId, removeEdge } from "./edgeGraph";
import { FlowCanvas } from "./FlowCanvas";
import { Inspector } from "./Inspector";
import { layoutFlow } from "./layout";
import { addNode, nextNodeId, removeNode } from "./nodeGraph";
import { commitNodeDrag } from "./nodePosition";
import { Palette } from "./Palette";
import { chooseSimulationOutcome, startSimulation, type SimulationState } from "./simulate";
import { Simulator } from "./Simulator";
import type { FlowDocument, FlowNodePosition } from "./types";

type PublishState =
  | { status: "idle" }
  | { status: "publishing" }
  | { status: "success"; flowVersionId: string }
  | { status: "error"; message: string; details: ApiErrorDetail[] };

function describePublishError(error: unknown): { message: string; details: ApiErrorDetail[] } {
  if (error instanceof ApiError) {
    return { message: error.message, details: error.body.details ?? [] };
  }
  if (error instanceof Error) {
    return { message: error.message, details: [] };
  }
  return { message: "could not publish", details: [] };
}

// The three shipped, schema-valid examples (docs/plans/06) — read directly
// via the @examples alias rather than copied, so this preview can never
// drift from what schema-check and the Go executor actually run.
const EXAMPLES: { name: string; document: FlowDocument }[] = [
  { name: "outage-notify", document: outageNotify as FlowDocument },
  { name: "bill-reminder", document: billReminder as FlowDocument },
  { name: "inbound-restored", document: inboundRestored as FlowDocument },
];
const FIRST_EXAMPLE = EXAMPLES[0]!;

// A freshly connected edge always starts with this port name — the
// common case for most node types' primary exit — and is a text field
// away from whatever it should really be (Inspector's EdgePortEditor).
const DEFAULT_NEW_EDGE_PORT = "next";

/**
 * Picks one shipped example flow, renders it, and lets a selected node or
 * edge be edited.
 *
 * Edits are local-only React state — nothing is sent to a backend, and
 * switching the example dropdown discards any in-progress edit and
 * reloads the canonical fixture (including its own undo history — a fresh
 * document starts with a fresh, empty history, not a carried-over one).
 * Add/remove nodes (palette + Inspector's own Delete button) and
 * connect/edit/remove edges now exist; persistence/publish does not.
 *
 * `document` is a linear undo/redo history's own `present` value
 * (documentHistory.ts), not a plain `useState`, so every one of this
 * component's own edits — drag, add/remove, connect/disconnect, an
 * Inspector Apply — is one undo step. Selection is deliberately cleared on
 * undo/redo rather than preserved: the previously-selected node or edge may
 * not exist in the restored document (an undo can bring back a just-deleted
 * node, or make a just-added one disappear), and Inspector's own
 * `key={selectedNode.id}` remount is what resets a typed form's in-progress
 * draft to match whatever the canvas now shows — skipping that reset would
 * leave an editor open on stale, no-longer-current data.
 *
 * `simulation` (Simulator.tsx, simulate.ts) is a structural "test call"
 * walkthrough of the current document's own edges — not a real execution
 * proof — kept in this component alongside `document`/`history`/selection
 * for the same reason: any document edit, undo/redo or example switch ends
 * an in-progress test call explicitly, since simulate.ts's own state is a
 * snapshot of whatever graph existed when the call started and would
 * silently describe a document that no longer exists otherwise. The
 * simulation's current node (while still running) is passed to FlowCanvas
 * as `activeNodeId`, a highlight independent of node/edge selection —
 * running a test call does not select anything for editing.
 *
 * The document (and which example it started from) is autosaved to
 * `localStorage` on every change (draftStorage.ts) — one slot, not one per
 * example, so a reload restores whatever was most recently being worked
 * on. The undo history itself is NOT part of that snapshot: a restored
 * session starts a fresh history from the restored document, exactly as
 * switching examples already does, rather than trying to serialize
 * `documentHistory.ts`'s own past/future arrays too.
 */
export function FlowPreview() {
  const [selectedName, setSelectedName] = useState(() => loadDraft(window.localStorage)?.exampleName ?? FIRST_EXAMPLE.name);
  const [history, setHistory] = useState(() =>
    initHistory(loadDraft(window.localStorage)?.document ?? FIRST_EXAMPLE.document),
  );
  // Only worth announcing when the restored document actually diverges from
  // the canonical example it started from — a Reset (or a reload of a
  // never-edited session) also goes through this same saved slot, and
  // "restored your draft" would be a misleading thing to say about content
  // that is byte-identical to the shipped fixture.
  const [restoredDraft, setRestoredDraft] = useState(() => {
    const saved = loadDraft(window.localStorage);
    if (!saved) {
      return false;
    }
    const canonical = EXAMPLES.find((example) => example.name === saved.exampleName)?.document;
    return JSON.stringify(saved.document) !== JSON.stringify(canonical);
  });
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [simulation, setSimulation] = useState<SimulationState | null>(null);
  const [publishState, setPublishState] = useState<PublishState>({ status: "idle" });
  const document = history.present;
  const positions = useMemo(() => layoutFlow(document), [document]);

  // Every document mutation is a deliberate, discrete edit already (drag-
  // stop, add/remove, connect, an Inspector Apply — never a raw keystroke),
  // so this needs no debouncing of its own; re-saving the just-restored
  // (or still-canonical) document on mount is a harmless no-op write.
  useEffect(() => {
    saveDraft(window.localStorage, { exampleName: selectedName, document });
  }, [selectedName, document]);

  // Every existing call site already passes either a document or a
  // `(current) => next` updater (the same shape plain `useState`'s own
  // setter accepts) — this wraps that in a history push instead of a plain
  // replace, so undo/redo needed no changes anywhere else in this file.
  // Any edit also ends an in-progress test call: simulate.ts's own state
  // (which node it is on, what edges it already walked) is a snapshot of
  // the document at the moment the call started, and would silently
  // describe a graph that no longer exists.
  function setDocument(next: FlowDocument | ((current: FlowDocument) => FlowDocument)) {
    setHistory((current) => {
      const resolved = typeof next === "function" ? next(current.present) : next;
      return pushHistory(current, resolved);
    });
    setSimulation(null);
    setPublishState({ status: "idle" });
  }

  const undo = useCallback(() => {
    setHistory(undoHistory);
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setSimulation(null);
  }, []);

  const redo = useCallback(() => {
    setHistory(redoHistory);
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setSimulation(null);
  }, []);

  // A global shortcut, not a `<textarea>`-scoped one: skipped entirely
  // while focus is inside an input/textarea/select, so the browser's own
  // native undo inside a field being actively typed into is never fought
  // over — this only ever undoes/redoes a whole applied document edit
  // (an Apply click, a drag, a connect), never in-progress keystrokes.
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const activeTag = window.document.activeElement?.tagName.toLowerCase();
      if (activeTag === "input" || activeTag === "textarea" || activeTag === "select") {
        return;
      }
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "z") {
        return;
      }
      event.preventDefault();
      if (event.shiftKey) {
        redo();
      } else {
        undo();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [undo, redo]);

  async function handlePublish() {
    setPublishState({ status: "publishing" });
    try {
      const csrfToken = readCsrfCookie(window.document.cookie);
      const published = await api.publishFlow(csrfToken, document.id, document);
      setPublishState({ status: "success", flowVersionId: published.flow_version_id });
    } catch (err) {
      setPublishState({ status: "error", ...describePublishError(err) });
    }
  }

  function selectExample(name: string) {
    const example = EXAMPLES.find((candidate) => candidate.name === name) ?? FIRST_EXAMPLE;
    setSelectedName(example.name);
    setHistory(initHistory(example.document));
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setSimulation(null);
    setRestoredDraft(false);
    setPublishState({ status: "idle" });
  }

  function startTestCall() {
    setSimulation(startSimulation(document));
  }

  function chooseTestCallOutcome(outcome: string) {
    setSimulation((current) => (current ? chooseSimulationOutcome(document, current, outcome) : current));
  }

  function stopTestCall() {
    setSimulation(null);
  }

  function handleSelectNode(nodeId: string | null) {
    setSelectedNodeId(nodeId);
    setSelectedEdgeId(null);
  }

  function handleSelectEdge(edgeId: string | null) {
    setSelectedEdgeId(edgeId);
    setSelectedNodeId(null);
  }

  function handleNodeDragStop(nodeId: string, newPosition: FlowNodePosition) {
    setDocument((current) => commitNodeDrag(current, positions, nodeId, newPosition));
  }

  function handleAddNode(type: string, config: unknown) {
    const id = nextNodeId(document, type);
    setDocument((current) => addNode(current, positions, id, type, config));
    handleSelectNode(id);
  }

  function handleRemoveNode(nodeId: string) {
    setDocument((current) => removeNode(current, nodeId));
    handleSelectNode(null);
  }

  function handleConnect(source: string, target: string) {
    const id = nextEdgeId(document);
    setDocument((current) => addEdge(current, id, source, DEFAULT_NEW_EDGE_PORT, target));
    handleSelectEdge(id);
  }

  function handleRemoveEdge(edgeId: string) {
    setDocument((current) => removeEdge(current, edgeId));
    handleSelectEdge(null);
  }

  return (
    <section aria-labelledby="flow-preview-heading" className="card">
      <h2 id="flow-preview-heading">Flow designer (preview)</h2>
      <p className="card__note">
        Renders a shipped example exactly as the Go executor and schema-check run it. Click a node
        or edge to view and edit it, drag a node to reposition it, or drag between two nodes'
        handles to connect them. Add a node from the palette. Publish validates the document
        against this tenant's own real registered references (below) and, only if it passes,
        persists it as a new, immutable flow version. Edits autosave to this browser, so a reload
        restores whatever you were last working on.
      </p>
      {restoredDraft ? (
        <p className="card__note">
          Restored your unsaved draft from <code>{selectedName}</code>.{" "}
          <button type="button" onClick={() => setRestoredDraft(false)}>
            Dismiss
          </button>
        </p>
      ) : null}
      <div className="flow-toolbar">
        <label>
          Example flow{" "}
          <select value={selectedName} onChange={(event) => selectExample(event.target.value)}>
            {EXAMPLES.map((example) => (
              <option key={example.name} value={example.name}>
                {example.name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => selectExample(selectedName)}
          title="Discard changes and reload the canonical example"
        >
          Reset
        </button>
        <button type="button" onClick={undo} disabled={!canUndo(history)} title="Undo (Ctrl/Cmd+Z)">
          Undo
        </button>
        <button type="button" onClick={redo} disabled={!canRedo(history)} title="Redo (Shift+Ctrl/Cmd+Z)">
          Redo
        </button>
        <button
          type="button"
          onClick={() => void handlePublish()}
          disabled={publishState.status === "publishing"}
        >
          {publishState.status === "publishing" ? "Publishing…" : "Publish"}
        </button>
      </div>
      {publishState.status === "success" ? (
        <p className="card__note" role="status">
          Published as flow version <code>{publishState.flowVersionId}</code>.
        </p>
      ) : null}
      {publishState.status === "error" ? (
        <div className="card__error" role="alert">
          <p>{publishState.message}</p>
          {publishState.details.length > 0 ? (
            <ul>
              {publishState.details.map((d, i) => (
                <li key={i}>
                  {d.node_id ? <code>{d.node_id}</code> : null} {d.detail ?? d.code}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      <div className="flow-designer">
        <Palette onAddNode={handleAddNode} />
        <FlowCanvas
          document={document}
          positions={positions}
          selectedNodeId={selectedNodeId}
          selectedEdgeId={selectedEdgeId}
          activeNodeId={simulation && !simulation.ended ? simulation.currentNodeId : null}
          onSelectNode={handleSelectNode}
          onSelectEdge={handleSelectEdge}
          onNodeDragStop={handleNodeDragStop}
          onConnect={handleConnect}
        />
        <Inspector
          document={document}
          selectedNodeId={selectedNodeId}
          selectedEdgeId={selectedEdgeId}
          onChange={setDocument}
          onRemoveNode={handleRemoveNode}
          onRemoveEdge={handleRemoveEdge}
        />
      </div>
      <Simulator
        simulation={simulation}
        onStart={startTestCall}
        onChoose={chooseTestCallOutcome}
        onStop={stopTestCall}
      />
    </section>
  );
}
